import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {isMobile} from 'react-device-detect';
import {
   ISmartPlayerGA4Event,
   ISmartPlayerAnalyticsEvent,
   ISmartPlayerRumDataEvent,
   ISmartPlayerTimeUpdateEvent,
   events,
   log,
   mediaInterfaceConstants,
   preloadOptions
} from '@techsmith/techsmith-smartplayer';
import {StringParam, useQueryParam} from 'use-query-params';
import SmartPlayerConstants from '../../constants/smartPlayerConstants';
import Analytics, {IUaEventOptions, IUAEventParams} from '../../service/analytics';
import config from '../../service/config';
import logging from '../../service/logging';
import MediaApi from '../../service/mediaApi';
import resumablePlayback from '../../service/resumablePlayback';
import StringUtil from '../../util/StringUtil';
import '@techsmith/techsmith-smartplayer/dist/techsmith-smart-player.css';
import '../../static/css/modal.less';
import Constants, {AnnotationModes, EmbedPlatforms, EntryPoint} from '../../constants/Constants';
import CommentMarkers from '../conversation/CommentMarkers';
import MarketingEmbedPlugin from '../marketing/MarketingEmbedPlugin';
import {showEmbeddedMarketing} from '../../util/MarketingHelper';
import ReactDOM from 'react-dom';
import Annotation from '../conversation/Annotation';
import {TscThemeName} from '@techsmith/tsc-cloud-style-guide';
import {annotationManagerStore, updateAnnotationManagerState} from '../../context/annotationManagerProvider';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {IComment} from '../../model/commentModel';
import {selectedConversationStore, updateSelectedConversationState} from '../../context/selectedConversationProvider';
import {updateDisableConversationState} from '../../context/disableConversationProvider';
import TimeFormatter from '../../util/TimeFormatter';
import {useUserProfileData} from '../../context/userProfileProvider';
import {commentsStore} from '../../context/commentsProvider';
import {useTscTheme} from '../../context/themeProvider';
import {updateQuizData} from '../../context/quizDataProvider';
import {mediaStore, updateMediaStateFromServer} from '../../context/mediaProvider';
import cssConstants from '../../constants/cssConstants';
import ReactionsPlugin from '../reactions/ReactionsPlugin';
import ReactionsPlaybackPlugin from '../reactions/ReactionsPlaybackPlugin';
import {EnvironmentType} from '../../model/appModel';
import CaptionDisplayStatusPlugin from './CaptionDisplayStatusPlugin';
import {CaptioningTypes, ITranscript} from '../../model/captionModels';
import {IMediaChapters} from '../../model/mediaDetails';
import {importMediaInterface} from '../../hooks/dynamicImport';
import {generateTranscriptFromCaptions} from '../../util/TranscriptGenerator';
import {transcriptStore, updateTranscriptDataState} from '../../context/transcriptProvider';

const smartPlayerApp = document.createElement('div');
smartPlayerApp.id = 'tscMediaContent';
smartPlayerApp.style.margin = 'auto';
let started = false;
let lastSavedTime = 0;
const timeBuffer = 3;

const SmartPlayerWrapper = styled.div<{isEmbedded: boolean}>`
   display: flex;
   justify-content: center;
   width: ${props => props.isEmbedded ? '100vw' : '100%'};
   height: ${props => props.isEmbedded ? '100vh' : '100%'};
   min-height: 0; // Safe help for smartplayer to not blowout the media-base container grid

   .annotation-wrapper {
      height: 100%;
      overflow: hidden;
   }

   .reactions-playback-wrapper {
      height: 100%;
      overflow: hidden;
   }
`;

const SmartPlayerContainerDiv = styled.div<{annotationMode: AnnotationModes; isEmbedView: boolean; isImage: boolean; isYouTube: boolean; isQuizUserSelfIdentifyOpen: boolean}>`
   width: 100%;

   .tsc-smartplayer {
      color: #fff;

      .video-wrapper {
         ${props => props.isQuizUserSelfIdentifyOpen ? `width: ${cssConstants.smartPlayerQuizSetupWidth}` : ''};
         ${props => props.isQuizUserSelfIdentifyOpen ? `height: ${cssConstants.smartPlayerQuizSetupHeight}` : ''};
      }

      .display-image-quiz-button, .question-set-container {
         position: absolute !important;
      }

      video {
         background-color: #000;
         color: #fff;
      }

      /* this is needed so the SP controls dont beat out the media tabs on narrow-tall media in */
      /* iOS safari which didn't seem to be respecting calculated height properly, before final calculations */
      /* i saw evidence of this behavior in chrome as well, so set for all mobile just to be sure */
      ${isMobile ? `.video-controls-container {
         z-index: auto !important;

         .progress-bar-control {
            z-index: auto !important;
         }
      }` : ''}

      #controls, .video-controls-container {
         visibility: ${props => props.annotationMode !== AnnotationModes.edit ? 'visible' : 'hidden'};
      }

      ${props => !props.isEmbedView && !props.isImage ? `min-width: ${cssConstants.smartPlayerMinWidth};` : ''} /* smartplayer itself needs min width to show usable control-bar for non-images, we should not need to use smartPlayerMinWidth elsewhere */
   }
`;

export const SmartPlayerBase: React.FC<ISmartPlayerProps & IStateMappedProps> = ({isEmbedded, embedPlatform, onDisplayModeChange, onMouseEnter, onMouseLeave, annotationMode, SelectedComment, comments, ContentUrl, Hash, IsImage, IsVideo, IsYouTube, Title, Description, HlsTokenInjectionProxyContentUrl, PosterUrl, SpriteUrl, AllowViewerToFastForwardVideo, XmpUrl, AudioDescriptionUrl, QuizId, Rotate, PreviewThumbnailsUri, PreviewThumbnailsInterval, PreviewThumbnailsMaxColumns, PreviewThumbnailsThumbHeight, PreviewThumbnailsThumbWidth, CaptioningType, Chapters, transcript}) => {
   const theme = useTscTheme();
   const [smartPlayerTimeQueryString] = useQueryParam(Constants.queryParams.smartPlayerStartTime, StringParam);
   const [smartPlayerTimeQueryStringUppercase] = useQueryParam(Constants.queryParams.smartPlayerStartTimeUppercase, StringParam);
   const [commentId] = useQueryParam(Constants.queryParams.commentId, StringParam);
   const smartPlayerContainer = useRef<HTMLDivElement>(null);
   const [commentMarkerDomElement, setCommentMarkerDomElement] = useState(null);
   const [annotationDomElement, setAnnotationDomElement] = useState(null);
   const [reactionsDomElement, setReactionsDomElement] = useState(null);
   const [reactionsPlaybackDomElement, setReactionsPlaybackDomElement] = useState(null);
   const [marketingMediaDomElement, setMarketingMediaDomElement] = useState(null);
   const [captionDisplayStatusDomElement, setCaptionDisplayStatusDomElement] = useState(null);
   const [mediaDuration, setMediaDuration] = useState(0);
   const [showMarketingPlugin, setShowMarketingPlugin] = useState<boolean>(true);
   const [userHasHiddenMarketing, setUserHasHiddenMarketing] = useState<boolean>(false);
   const [quizUserSelfIdentifyIsOpen, setQuizUserSelfIdentifyIsOpen] = useState(false);
   const [smartPlayerMediaError, setSmartPlayerMediaError] = useState(false);
   const {isWorking: userProfileIsLoading, userProfile} = useUserProfileData();

   useEffect(() => {
      void importMediaInterface().then(mediaInterface => {
         console.info(`%cUsing TechSmith Smart Player version: ${mediaInterface.version}`, 'color:blue; font-size:18px');
      });
   }, []);

   const conditionallyConfigurePlaybackResume = async () => {
      if (smartPlayerTimeQueryStringUppercase || smartPlayerTimeQueryString || commentId) {
         return;
      }

      const mediaInterface = await importMediaInterface();
      const savedTime = resumablePlayback.getProgress(config.user.TechSmithId, Hash);
      const isTimeSafeToSeek = savedTime + timeBuffer === mediaInterface.getSafeSeekTime(savedTime + timeBuffer);

      if (isTimeSafeToSeek) {
         mediaInterface.currentTime = savedTime;
      }
   };

   const recordGAViewEvent = () => {
      let playerOrigin: string;
      if (!window.document.referrer) {
         playerOrigin = SmartPlayerConstants.analytics.viewAction.FromUnknown;
      } else if (RegExp(config.environmentData.GoogleAnalyticsDomain).exec(window.document.referrer) === null) {
         playerOrigin = SmartPlayerConstants.analytics.viewAction.FromEmbed;
      } else {
         playerOrigin = SmartPlayerConstants.analytics.viewAction.FromViewPage;
      }

      /* eslint-disable camelcase */
      const ga4Event: IUaEventOptions = {
         eventName: `${Constants.analytics.SmartPlayerEventsPrefix}${SmartPlayerConstants.analytics.viewEvent}`,
         eventParams: {
            player_origin: playerOrigin,
            media_hash: Hash
         } as IUAEventParams
      };
      /* eslint-enable */

      Analytics.gaEvent(ga4Event);
   };

   const videoStartEventListener = async () => {
      const mediaInterface = await importMediaInterface();
      recordGAViewEvent();
      void MediaApi.incrementView(Hash); // intentionally not-awaited
      if (!IsYouTube) {
         mediaInterface.controls = mediaInterfaceConstants.controls.autohide;
      }
      mediaInterface.removeEventListener(events.External.VideoPlay, videoStartEventListener);
   };

   const mediaReadyEventListener = () => {
      void (IsImage && MediaApi.incrementView(Hash));
      updateDisableConversationState({isAnnotatingDisabled: false, isCommentingDisabled: false});
   };

   const displayModeChangedEventListener = (displayMode: string) => {
      onDisplayModeChange && onDisplayModeChange(displayMode);
   };

   const addEventListeners = async () => {
      if (log && log.addEventListener) {
         log.addEventListener('EXCEPTION', e => {
            logging.warn('Encountered exception in Smart Player', e);
         });
      }

      const mediaInterface = await importMediaInterface();

      mediaInterface.addEventListener(events.External.ShowStartQuizPrompt, () => {
         setQuizUserSelfIdentifyIsOpen(true);
      });

      mediaInterface.addEventListener(events.External.HideStartQuizPrompt, () => {
         setQuizUserSelfIdentifyIsOpen(false);
      });

      mediaInterface.addEventListener(events.External.QuizVisible, () => {
         updateQuizData({IsQuizzing: true});
         if (!IsYouTube) {
            mediaInterface.controls = mediaInterfaceConstants.controls.true;
         }
      });

      mediaInterface.addEventListener(events.External.QuizHidden, () => {
         updateQuizData({IsQuizzing: false});
         if (!IsYouTube) {
            mediaInterface.controls = mediaInterfaceConstants.controls.autohide;
         }
      });

      mediaInterface.addEventListener(events.ExternalVideo.VideoLoadedData, conditionallyConfigurePlaybackResume);

      mediaInterface.addEventListener(events.External.VideoEnd, () => {
         updateDisableConversationState({isAnnotatingDisabled: true});
      });

      mediaInterface.addEventListener(events.External.VideoReplay, () => {
         updateDisableConversationState({isAnnotatingDisabled: false});
      });

      mediaInterface.addEventListener(events.ExternalVideo.VideoLoadedMetadata, () => {
         setMediaDuration(mediaInterface.duration);
      });

      mediaInterface.addEventListener(events.External.CaptionsReady, () => {
         if (!transcript) {
            const mediaTranscript = generateTranscriptFromCaptions(mediaInterface.captions?.captions);
            updateTranscriptDataState({transcript: mediaTranscript});
         }
      });

      mediaInterface.addEventListener(events.External.MediaError, () => {
         Analytics.aiEvent({
            name: 'SmartPlayerEvent',
            label: 'SmartPlayerMediaError',
            value: Hash
         });
         setSmartPlayerMediaError(true);
      });

      mediaInterface.addEventListener(events.External.PlayerControlsHidden, () => {
         setShowMarketingPlugin(false);
      });

      mediaInterface.addEventListener(events.External.PlayerControlsVisible, () => {
         setShowMarketingPlugin(true);
      });

      mediaInterface.addEventListener<ISmartPlayerTimeUpdateEvent>(events.ExternalVideo.VideoTimeUpdate, () => {
         const roundedTime = Math.floor(mediaInterface.currentTime);

         if (roundedTime !== lastSavedTime) {
            resumablePlayback.setProgress(config.user.TechSmithId, Hash, roundedTime.toString());
            lastSavedTime = roundedTime;
         }
      });

      mediaInterface.addEventListener(events.External.VideoPlay, videoStartEventListener);
      mediaInterface.addEventListener(events.External.MediaReady, mediaReadyEventListener);

      mediaInterface.addEventListener(events.External.DisplayModeChanged, displayModeChangedEventListener);

      mediaInterface.addEventListener<ISmartPlayerGA4Event>(events.External.GoogleAnalytics4Event, e => {
         const smartPlayerEvent = `${Constants.analytics.SmartPlayerEventsPrefix}${e.eventName}`;

         /* eslint-disable camelcase */
         const ga4Event: IUaEventOptions = {
            eventName: smartPlayerEvent,
            eventParams: {
               media_hash: Hash
            } as IUAEventParams
         };
         /* eslint-enable */

         if (e.eventParams) {
            Object.keys(e.eventParams).forEach(key => {
               ga4Event.eventParams[key] = e.eventParams[key];
            });
         }

         Analytics.gaEvent(ga4Event);
      });

      mediaInterface.addEventListener<ISmartPlayerAnalyticsEvent>(events.External.AnalyticsEvent, e => {
         // the events coming out of SP aren't reliable enough not to sanitize, what should be all strings (and number for value)
         // are coming out booleans, numbers, undefined or otherwise unpredictable and causing our code to throw errors without this
         const sanitizedEvent: ISmartPlayerAnalyticsEvent = {
            category: JSON.stringify(e.category),
            action: JSON.stringify(e.action),
            label: JSON.stringify(e.label),
            value: e.value
         };

         Analytics.aiEvent({
            name: 'SmartPlayerEvent',
            label: sanitizedEvent.action,
            value: sanitizedEvent.label
         });
      });

      mediaInterface.addEventListener<ISmartPlayerRumDataEvent>(events.External.PlayerRumData, e => {
         const categorySuffix = e.categorySuffix || '';
         if (e?.rum) {
            e.rum.forEach(rumItem => {
               Analytics.aiMeasurement({
                  name: `SmartPlayerTiming_PresentationWebClient${categorySuffix}`,
                  label: rumItem.name,
                  value: rumItem.time
               });
            });
         }
      });
   };

   const configure = async () => {
      const mediaInterface = await importMediaInterface();
      // the .replace is for older youtube content urls that may be non-secure protocol
      mediaInterface.addSrc(ContentUrl.replace(/http:\/\//i, 'https://'));
      if (HlsTokenInjectionProxyContentUrl) {
         mediaInterface.addSrc(HlsTokenInjectionProxyContentUrl);
      }
      mediaInterface.disableContinueWithoutQuestions = true;
      mediaInterface.posterSrc = PosterUrl;
      mediaInterface.tocSrc = SpriteUrl;
      mediaInterface.rememberUserInfo = true;

      if (!isEmbedded && config.featureSwitches.IsScreencast) {
         mediaInterface.enableTheaterMode = true;
      }

      mediaInterface.allowFastForward = AllowViewerToFastForwardVideo;

      if (!StringUtil.isNullOrWhiteSpace(XmpUrl)) {
         mediaInterface.xmpSrc = XmpUrl;
      }

      if (!StringUtil.isNullOrWhiteSpace(AudioDescriptionUrl)) {
         mediaInterface.audioDescriptionMediaSrc = AudioDescriptionUrl;
      }

      if (embedPlatform === EmbedPlatforms.slack) {
         mediaInterface.disablePictureInPicture = true;
      }

      if (!isEmbedded || embedPlatform === EmbedPlatforms.slack) {
         mediaInterface.showAcknowledgementLink = false;
      }

      mediaInterface.showADACompliantCaptions = !!config.featureSwitches.ForceADACompliantCaptionStyle;

      if (config.environmentData.Environment !== EnvironmentType.live) {
         mediaInterface.quizServiceBaseUrl = config.environmentData.QuizzingServiceUrl;
         mediaInterface.resultsServiceBaseUrl = config.environmentData.ResultsRootUrl;
      }

      mediaInterface.quizCreateInstanceUrl = config.environmentData.QuizzingServiceCreateInstanceUrl;

      if (!StringUtil.isNullOrWhiteSpace(QuizId)) {
         mediaInterface.quizId = QuizId;
      }

      if (Rotate) {
         mediaInterface.rotation = Rotate;
      }

      if (IsYouTube) {
         mediaInterface.controls = SmartPlayerConstants.youtubeControls;
      } else {
         if (PreviewThumbnailsUri) {
            mediaInterface.videoThumbnailsSrc = PreviewThumbnailsUri;
            mediaInterface.videoThumbnailWidth = PreviewThumbnailsThumbWidth;
            mediaInterface.videoThumbnailHeight = PreviewThumbnailsThumbHeight;
            mediaInterface.videoThumbnailInterval = PreviewThumbnailsInterval;
            mediaInterface.videoThumbnailColumns = PreviewThumbnailsMaxColumns;
         }

         mediaInterface.useLargerProgressBar = false;
         // the watch page must 'disable' the preload play button overlay for the sake of conversation interaction (also force controls on)
         if (config.entryPoint === EntryPoint.watch) {
            mediaInterface.preload = preloadOptions.metadata;
            mediaInterface.controls = mediaInterfaceConstants.controls.true;
         }

         if (isEmbedded) {
            mediaInterface.customFullScreenDomElement = document.getElementsByClassName(Constants.mainElementClassName)[0];
         }
      }

      if (IsVideo && Chapters || !StringUtil.isNullOrWhiteSpace(Description)) {
         if (Chapters) {
            const chaptersString = Chapters.chapters.map(chapter => `${TimeFormatter.formatTime(chapter.timeInMilliseconds / 1000)} ${chapter.label}`).join('\n');
            mediaInterface.createTocFromDescription(encodeURIComponent(chaptersString));
         } else {
            mediaInterface.createTocFromDescription(encodeURIComponent(Description));
         }
      }

      mediaInterface.background = IsImage ? theme === TscThemeName.dawn ? SmartPlayerConstants.imageBackgroundColorDawn : SmartPlayerConstants.imageBackgroundColorDusk : SmartPlayerConstants.videoBackgroundColor;

      mediaInterface.contextMenu = false;

      // need this or camtasia produced recordings that set unicodeenabled=true in xmp will break SAS tokens as they are set up in smartplayer (JMD: 2019-08)
      mediaInterface.ignoreXmpControllerParameters = ['unicodeenabled', 'autohide', 'backgroundcolor'];

      await setUserInfo();

      mediaInterface.userSignedIn = config.user.IsSignedIn && (config.watchConfig?.isOwner || config.embedConfig?.isOwner || !config.featureSwitches.DisableSmartPlayerAccessToken);
   };

   const setUserInfo = async () => {
      const mediaInterface = await importMediaInterface();
      if (userProfile) {
         mediaInterface.userInfo = {
            firstName: userProfile.firstName || '',
            lastName: userProfile.lastName || '',
            email: config.user.EmailAddress || ''
         };
      }
   };

   const run = async () => {
      const mediaInterface = await importMediaInterface();
      if (!started) {
         started = true;
         await addEventListeners();
         mediaInterface.mediaTitle = Title;
         mediaInterface.run('tscMediaContent');

         if (mediaInterface.controls !== SmartPlayerConstants.youtubeControls) {
            if (config.featureSwitches.EnableReactions && IsVideo) {
               const reactionsPluginInfo = await mediaInterface.registerPlugin('techsmithReactions', Constants.pluginElements.progressBar);
               reactionsPluginInfo.pluginContainerElement.classList.add('reactions-track');
               setReactionsDomElement(reactionsPluginInfo.pluginContainerElement);

               const reactionsPlaybackPluginInfo = await mediaInterface.registerPlugin('techsmithReactionsPlayback', Constants.pluginElements.player);
               reactionsPlaybackPluginInfo.pluginContainerElement.classList.add('reactions-playback-wrapper');
               setReactionsPlaybackDomElement(reactionsPlaybackPluginInfo.pluginContainerElement);
            }

            const commentMarkerPluginInfo = IsVideo && await mediaInterface.registerPlugin('knowmiaCommentMarkers', Constants.pluginElements.progressBar);
            const annotationPluginInfo = await mediaInterface.registerPlugin('knowmiaAnnotation', Constants.pluginElements.media);
            IsVideo && commentMarkerPluginInfo.pluginContainerElement.classList.add('comment-track');
            annotationPluginInfo.pluginContainerElement.classList.add('annotation-wrapper');
            setCommentMarkerDomElement(commentMarkerPluginInfo.pluginContainerElement);
            setAnnotationDomElement(annotationPluginInfo.pluginContainerElement);

            if (isEmbedded && showEmbeddedMarketing()) {
               const marketingPluginInfo = await mediaInterface.registerPlugin('knowmiaSnagitMarketing', Constants.pluginElements.player);
               setMarketingMediaDomElement(marketingPluginInfo.pluginContainerElement);
            }

            const captionDisplayStatusPluginInfo = IsVideo && await mediaInterface.registerPlugin('TechSmithCaptionDisplayStatus', Constants.pluginElements.player);
            IsVideo && captionDisplayStatusPluginInfo.pluginContainerElement.classList.add('caption-display-status');
            setCaptionDisplayStatusDomElement(captionDisplayStatusPluginInfo.pluginContainerElement);
         }
      }
   };

   const appendSmartPlayerApp = () => {
      if (smartPlayerContainer.current && !smartPlayerContainer.current.contains(smartPlayerApp)) {
         smartPlayerContainer.current.appendChild(smartPlayerApp);
      }
   };

   const onMarketingPluginClose = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.preventDefault();
      setUserHasHiddenMarketing(true);
   };

   const displayMarketingOverMedia = (): boolean => showMarketingPlugin && !userHasHiddenMarketing && !!marketingMediaDomElement;

   useEffect(() => {
      if (!userProfileIsLoading) {
         void setUserInfo();
      }
   }, [userProfileIsLoading]);

   useEffect(() => {
      void importMediaInterface().then(mediaInterface => {
         mediaInterface.setTheme(theme);
      });
   }, [theme]);

   useEffect(() => {
      const handleHideAnnotationsAndSelectComment = async () => {
         const mediaInterface = await importMediaInterface();
         const roundedTime = Math.floor(mediaInterface.currentTime);
         const commentToSelect = comments?.find(comment => Math.floor(comment.time) === roundedTime);

         if (Math.floor(SelectedComment?.time) !== Math.floor(commentToSelect?.time) && commentToSelect) {
            updateSelectedConversationState({SelectedComment: commentToSelect});
         }

         if (annotationMode === AnnotationModes.display) {
            const timeDifference = TimeFormatter.getRoundedTime(Math.abs(mediaInterface.currentTime - SelectedComment?.time));
            const timeMismatch = timeDifference > Constants.annotation.timeTolerance;

            if (timeMismatch) {
               updateAnnotationManagerState({annotationMode: AnnotationModes.none});
            }
         }
      };

      void importMediaInterface().then(mediaInterface => {
         mediaInterface.addEventListener<ISmartPlayerTimeUpdateEvent>(events.ExternalVideo.VideoTimeUpdate, handleHideAnnotationsAndSelectComment);
      });

      return () => {
         void importMediaInterface().then(mediaInterface => {
            mediaInterface.removeEventListener(events.ExternalVideo.VideoTimeUpdate, handleHideAnnotationsAndSelectComment);
         });
      };
   }, [SelectedComment, annotationMode, comments]);

   useEffect(() => {
      void importMediaInterface().then(mediaInterface => {
         mediaInterface.doubleClickControlEnabled = annotationMode !== AnnotationModes.edit;
      });
   }, [annotationMode]);

   useEffect(() => {
      if (smartPlayerMediaError) {
         updateMediaStateFromServer();
         setSmartPlayerMediaError(false);
      }
   }, [smartPlayerMediaError]);

   useEffect(() => {
      if (ContentUrl) {
         void importMediaInterface().then(async mediaInterface => {
            if (started) {
               mediaInterface.destroy();
               started = false;
            }
            await configure();
            appendSmartPlayerApp();
            await run();
         });
      }
   }, [ContentUrl]);

   return (
      <SmartPlayerWrapper
         isEmbedded={isEmbedded}
         data-test-id="smartplayer"
         onMouseEnter={onMouseEnter}
         onMouseLeave={onMouseLeave}
      >
         <SmartPlayerContainerDiv
            data-hj-suppress
            className="smart-player-container"
            ref={smartPlayerContainer}
            annotationMode={annotationMode}
            isEmbedView={isEmbedded}
            isImage={IsImage}
            isYouTube={IsYouTube}
            isQuizUserSelfIdentifyOpen={quizUserSelfIdentifyIsOpen}
         />
         {commentMarkerDomElement && ReactDOM.createPortal(<CommentMarkers mediaDuration={mediaDuration} />, commentMarkerDomElement as Element)}
         {reactionsDomElement && ReactDOM.createPortal(<ReactionsPlugin mediaDuration={mediaDuration} />, reactionsDomElement as Element)}
         {reactionsPlaybackDomElement && ReactDOM.createPortal(<ReactionsPlaybackPlugin mediaDuration={mediaDuration} />, reactionsPlaybackDomElement as Element)}
         {annotationDomElement && ReactDOM.createPortal(<Annotation />, annotationDomElement as Element)}
         {CaptioningType === CaptioningTypes.AutoTranscribed && captionDisplayStatusDomElement && ReactDOM.createPortal(<CaptionDisplayStatusPlugin />, captionDisplayStatusDomElement as Element)}
         {displayMarketingOverMedia() && ReactDOM.createPortal(
            <MarketingEmbedPlugin
               canClose={true}
               linkToMoreInfo={embedPlatform !== EmbedPlatforms.slack}
               onClose={onMarketingPluginClose}
            />, marketingMediaDomElement as Element)}
      </SmartPlayerWrapper>
   );
};

export interface ISmartPlayerProps {
   isEmbedded: boolean;
   embedPlatform?: EmbedPlatforms;
   onDisplayModeChange?: (displayMode: string) => void;
   onMouseEnter?: () => void;
   onMouseLeave?: () => void;
}

export interface IStateMappedProps {
   annotationMode: AnnotationModes;
   SelectedComment: IComment;
   comments: IComment[];
   ContentUrl: string;
   Hash: string;
   IsImage: boolean;
   IsVideo: boolean;
   IsYouTube: boolean;
   Title: string;
   Description: string;
   HlsTokenInjectionProxyContentUrl: string;
   PosterUrl: string;
   SpriteUrl: string;
   AllowViewerToFastForwardVideo: boolean;
   XmpUrl: string;
   AudioDescriptionUrl: string;
   QuizId: string;
   Rotate: number;
   PreviewThumbnailsUri: string;
   PreviewThumbnailsInterval: number;
   PreviewThumbnailsMaxColumns: number;
   PreviewThumbnailsThumbHeight: number;
   PreviewThumbnailsThumbWidth: number;
   CaptioningType: CaptioningTypes;
   Chapters?: IMediaChapters;
   transcript: ITranscript;
}

const mapStatesToProps = (
   {isEmbedded, embedPlatform, onDisplayModeChange, onMouseEnter, onMouseLeave}: ISmartPlayerProps,
   {annotationMode}: Partial<IStateMappedProps>,
   {SelectedComment}: Partial<IStateMappedProps>,
   {comments}: Partial<IStateMappedProps>,
   {ContentUrl, Hash, IsImage, IsVideo, IsYouTube, Title, Description, HlsTokenInjectionProxyContentUrl, PosterUrl, SpriteUrl, AllowViewerToFastForwardVideo, XmpUrl, AudioDescriptionUrl, QuizId, Rotate, PreviewThumbnailsUri, PreviewThumbnailsInterval, PreviewThumbnailsMaxColumns, PreviewThumbnailsThumbHeight, PreviewThumbnailsThumbWidth, CaptioningType, Chapters}: Partial<IStateMappedProps>,
   {transcript}: Partial<IStateMappedProps>
): IStateMappedProps & ISmartPlayerProps => ({isEmbedded, embedPlatform, onDisplayModeChange, onMouseEnter, onMouseLeave, annotationMode, SelectedComment, comments, ContentUrl, Hash, IsImage, IsVideo, IsYouTube, Title, Description, HlsTokenInjectionProxyContentUrl, PosterUrl, SpriteUrl, AllowViewerToFastForwardVideo, XmpUrl, AudioDescriptionUrl, QuizId, Rotate, PreviewThumbnailsUri, PreviewThumbnailsInterval, PreviewThumbnailsMaxColumns, PreviewThumbnailsThumbHeight, PreviewThumbnailsThumbWidth, CaptioningType, Chapters, transcript});

export default withMemoizedContexts(mapStatesToProps, annotationManagerStore, selectedConversationStore, commentsStore, mediaStore, transcriptStore)(SmartPlayerBase);
